import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { WebhookLogs_LogRow } from '../contracts/WebhookLogs_LogRow'

/**
 * useWebhookLogs — network owner for webhook-log operations.
 *
 * fetchLogs(provider) — GET index.php/webhook_logs/buscar[?provider=…].
 *   Owns the list read so the provider filter is a real query param on the
 *   request that leaves the app. `dados` is `{ logs: [...] }` per
 *   WebhookLogs_BuscarResponse — the array is unwrapped HERE, because
 *   DataTable's endpoint loader assumes `dados` IS the row array and threw
 *   `data.map is not a function` on every load of this screen.
 *
 * reprocess(id) — POST subaccounts/webhooks/reprocess/{id}.
 *   Triggers a manual retry of a failed/received webhook.
 *   Returns the server result object on success.
 *   Callers should refresh the log list / detail on success.
 *
 * _reset() — wipes module-scope state; call in afterEach for test isolation.
 */

// ── Module-scope singleton state ─────────────────────────────────────────────

const logs = ref<WebhookLogs_LogRow[]>([])
const loading = ref(false)
const loadError = ref<string | null>(null)
const reprocessing = ref(false)
const reprocessError = ref<string | null>(null)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useWebhookLogs() {
  /**
   * fetchLogs — GET index.php/webhook_logs/buscar, optionally filtered by provider.
   *
   * The provider is passed as an ARGUMENT, not read from a reactive prop the
   * grid re-reads on some later tick: the previous wiring set a ref and asked
   * the grid to reload on the same tick, so every request went out with the
   * PREVIOUS filter (or none at all). Taking the value explicitly removes the
   * tick from the equation entirely.
   */
  async function fetchLogs(provider?: string | null): Promise<WebhookLogs_LogRow[]> {
    loading.value = true
    loadError.value = null
    try {
      const query = provider && provider !== 'all' ? `?provider=${encodeURIComponent(provider)}` : ''
      const resp = await httpService.get(sistemaUrl(`index.php/webhook_logs/buscar${query}`), {
        logContext: 'useWebhookLogs',
        responseContract: 'WebhookLogs_BuscarResponse',
      })
      // `dados` is `{ logs: [...] }`, NOT the row array — unwrap it here.
      logs.value = resp.dados?.logs ?? []
      log('useWebhookLogs', 'fetchLogs', 'Logs carregados', {
        provider: provider ?? 'all',
        count: logs.value.length,
      })
      return logs.value
    } catch (err) {
      log('useWebhookLogs', 'fetchLogs', 'Erro ao carregar logs de webhook', {
        provider: provider ?? 'all',
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      logs.value = []
      loadError.value = (err as Error)?.message ?? 'Erro desconhecido'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * reprocess — POST subaccounts/webhooks/reprocess/{id}.
   *
   * Re-runs a webhook through the full normalization + confirmation pipeline.
   * Returns the server `dados` object containing at minimum `{ processed: boolean }`.
   */
  async function reprocess(id: number | string): Promise<Record<string, unknown>> {
    reprocessing.value = true
    reprocessError.value = null
    try {
      const raw = await httpService.post(
        sistemaUrl(`subaccounts/webhooks/reprocess/${id}`),
        {},
        { skipErrorDisplay: false },
      )
      const resp = raw as { dados: Record<string, unknown> }
      log('useWebhookLogs', 'reprocess', 'Webhook reprocessado', { id, result: resp.dados })
      return resp.dados ?? {}
    } catch (err) {
      log('useWebhookLogs', 'reprocess', 'Erro ao reprocessar webhook', {
        id,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      reprocessError.value = (err as Error)?.message ?? 'Erro desconhecido'
      throw err
    } finally {
      reprocessing.value = false
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    logs.value = []
    loading.value = false
    loadError.value = null
    reprocessing.value = false
    reprocessError.value = null
  }

  return {
    logs,
    loading,
    loadError,
    fetchLogs,
    reprocessing,
    reprocessError,
    reprocess,
    _reset,
  }
}
